import { captureException } from '@sentry/react'
import { getAIVideoProjects_API } from 'api'
import { ReduxThunkError } from 'utils/errors'
import { ApiError } from 'utils/errors'
import { cacheSelectors } from 'store/features/cache/selectors'
import { TAppDispatch, TGetState } from 'store/types'
import { IAIVideoGenerationParams, IAIVideoProject } from 'types/aiVideo'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { PROJECT_MEDIA_TYPES } from 'consts'
import {
  getDefaultAIVideoModelGenerationParams,
  getDefaultAIVideoModel,
  mapApiReferenceMediasToState,
  buildReferenceMediaFrameWhenMissingDimensions,
  resolveParentProjectId
} from 'store/features/aiVideo/utils'
import {
  filterDeletedReferenceImages,
  restoreFormPromptMentions
} from './utils'
import { withAppThunks } from 'store/with-app-thunks'
import aiVideoSlice from 'store/features/aiVideo/slice'
import { projectSelectors } from 'store/features/project/selectors'
import { aiVideoSelectors } from '../selectors'
const { actions } = aiVideoSlice

const fetchAIVideoProject = (assetId: number) =>
  withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: TGetState
      ): Promise<IAIVideoProject | null> => {
        const state = getState()
        const aiVideoCache = cacheSelectors.aiVideoCache(state)
        const existingItem = aiVideoCache[assetId]

        if (existingItem) {
          return await filterDeletedReferenceImages(existingItem)
        }

        try {
          const { data } = await getAIVideoProjects_API({
            projectIds: [assetId]
          })

          if (data.length === 0) {
            return null
          }

          const [rawProject] = data

          dispatch(appThunks.cacheThunks.addToAiVideoCache(data))

          return await filterDeletedReferenceImages(rawProject)
        } catch (error) {
          if (!(error instanceof ApiError)) {
            captureException(new ReduxThunkError(error))
          }
          return null
        }
      }
  )
const setGenerationParams = (params: IAIVideoGenerationParams) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    dispatch(actions.setGenerationParams(params))
    dispatch(actions.setHasUserSelectedImageAspectRatio(false))
    if (params.selectedModel) {
      dispatch(
        appThunks.aiVideoThunks.updateSelectedModel({
          modelName: params.selectedModel,
          duration: params.duration,
          quality: params.quality,
          aspectRatio: params.aspectRatio
        })
      )
    }
  })

const setGenerationParamsByAssetId =
  (assetId: number | string) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const numericAssetId = Number(assetId)
    if (isNaN(numericAssetId)) {
      return
    }
    try {
      const data = (await dispatch(
        fetchAIVideoProject(numericAssetId)
      )) as IAIVideoProject
      if (data) {
        const params: IAIVideoGenerationParams = {
          // The stored prompt holds the resolved wording; rebuild its chips
          // against the reference images this form actually received.
          prompt: restoreFormPromptMentions({
            prompt: data.prompt,
            sentProject:
              cacheSelectors.aiVideoCache(getState())[numericAssetId],
            loadedProject: data
          }),
          duration: data.videoDurationSeconds,
          selectedModel: data.videoModel,
          aspectRatio: data.aspectRatio,
          quality: data.quality,
          referenceMedias: mapApiReferenceMediasToState(
            data.referenceMedias,
            data.width !== null && data.height !== null
              ? { width: data.width, height: data.height }
              : undefined
          ),
          targetMediaItemId: null,
          aiImageId: data?.meta?.reference?.id || null,
          isConsistencyEnabled: false,
          smartPrompt: '',
          // Preserve the user's current power-mode choice — loading an
          // existing item shouldn't flip the toggle.
          isFastTrack: aiVideoSelectors.isFastTrack(getState()),
          refImageSelectedStyleId: data?.meta?.imageStyleId || undefined,
          parentProjectId: resolveParentProjectId(data)
        }
        dispatch(setGenerationParams(params))
      }
    } catch (error) {
      captureException(new ReduxThunkError(error))
    }
  }

const setRegenerationForm =
  // `historyItemId` lets callers populate the form from a specific asset
  // (e.g. a chosen version-history item) while still targeting the active
  // timeline scene via `mediaItemId`. When omitted, the active scene's own
  // asset is used.
  (mediaItemId: string, historyItemId?: number | string) =>
    async (dispatch: TAppDispatch, getState: TGetState) => {
      const state = getState()
      try {
        const mediaItems = projectDataSelectors.mediaItems(state)
        const projectAspectRatio =
          projectDataSelectors.defaultDimensions(state)?.aspectRatioString

        const targetMedia = mediaItems.find(
          item =>
            item.id === mediaItemId && item.type === PROJECT_MEDIA_TYPES.aiVideo
        )
        if (!targetMedia) {
          return
        }
        const numericAssetId = Number(historyItemId ?? targetMedia.assetId)

        if (isNaN(numericAssetId)) {
          dispatch(
            setGenerationParams({
              ...getDefaultAIVideoModelGenerationParams({ projectAspectRatio }),
              targetMediaItemId: mediaItemId
            })
          )
          return
        }

        const data = (await dispatch(
          fetchAIVideoProject(numericAssetId)
        )) as IAIVideoProject
        if (!data) {
          return
        }

        const mappedReferenceMedias = mapApiReferenceMediasToState(
          data.referenceMedias,
          undefined
        )
        const referenceMedias = mappedReferenceMedias?.frames?.length
          ? {
              ...mappedReferenceMedias,
              frames: await Promise.all(
                mappedReferenceMedias.frames.map(
                  buildReferenceMediaFrameWhenMissingDimensions
                )
              )
            }
          : mappedReferenceMedias

        dispatch(
          setGenerationParams({
            // The stored prompt holds the resolved wording; rebuild its chips
            // against the reference images this form actually received.
            prompt: restoreFormPromptMentions({
              prompt: data.prompt,
              sentProject:
                cacheSelectors.aiVideoCache(getState())[numericAssetId],
              loadedProject: data
            }),
            duration: data.videoDurationSeconds,
            selectedModel: data.videoModel,
            aspectRatio: data.aspectRatio,
            quality: data.quality,
            referenceMedias,
            targetMediaItemId: mediaItemId,
            // The generate-video backend does not persist `meta.reference`;
            // within a session the completion handler re-keys the request's
            // cache entry to the numeric id, so this fetch still sees it.
            aiImageId: data?.meta?.reference?.id || null,
            isConsistencyEnabled: false,
            smartPrompt: '',
            isFastTrack: aiVideoSelectors.isFastTrack(state),
            refImageSelectedStyleId: data?.meta?.imageStyleId || undefined,
            sourceProjectId: data?.meta?.sourceProjectId,
            parentProjectId: resolveParentProjectId(data),
            // When regenerating from a chosen version, preview that version's
            // video; otherwise let the overlay fall back to the active scene.
            previewVideoUrl:
              historyItemId !== undefined
                ? data.videoUrl || undefined
                : undefined
          })
        )
      } catch (error) {
        captureException(new ReduxThunkError(error))
      }
    }

const clearGenerationParams = () =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const models = aiVideoSelectors.aiVideoModels(state)
    const defaultSettings = state.aiVideo.defaultGenerationSettings
    const aiFlowVideoModelCode = projectSelectors.aiFlowVideoModelCode(state)
    const aiFlowVideoModel = aiFlowVideoModelCode
      ? (models.find(model => model.model === aiFlowVideoModelCode) ??
        undefined)
      : undefined

    const projectAspectRatio =
      projectDataSelectors.defaultDimensions(state)?.aspectRatioString

    // Resolution order: project-meta model → previously-saved settings from
    // this session → catalog default (flagged by `isDefaultFor: 'v-editor'`).
    dispatch(
      actions.clearGenerationParams({
        defaultModel: aiFlowVideoModel,
        projectAspectRatio
      })
    )

    if (aiFlowVideoModel) {
      dispatch(
        appThunks.aiVideoThunks.updateSelectedModel({
          modelName: aiFlowVideoModel.model
        })
      )
    } else if (defaultSettings?.selectedModel) {
      dispatch(
        appThunks.aiVideoThunks.updateSelectedModel({
          modelName: defaultSettings.selectedModel,
          duration: defaultSettings.duration || undefined,
          quality: defaultSettings.quality || undefined,
          aspectRatio: defaultSettings.aspectRatio || undefined
        })
      )
    } else {
      const defaultModel = getDefaultAIVideoModel(models)
      if (defaultModel) {
        dispatch(
          appThunks.aiVideoThunks.updateSelectedModel({
            modelName: defaultModel.model,
            duration: defaultSettings?.duration || undefined
          })
        )
      }
    }
  })

export const formThunks = {
  clearGenerationParams,
  getAIVideoByAssetId: fetchAIVideoProject,
  setGenerationParamsByAssetId,
  setRegenerationForm,
  setGenerationParams
}
